import React, { createContext, useCallback, useMemo, useState, PropsWithChildren } from 'react';

import { buildConsentValue, readConsentCookie, writeConsentCookie } from './consentCookie';
import { pushConsentUpdate } from './cookieConsentMode';
import { CONSENT_DECISION } from './constants';
import { ConsentDecision } from './types';

interface IConsentContextValue {
    decision: ConsentDecision;
    accept: () => void;
    deny: () => void;
}

export const ConsentContext = createContext<IConsentContextValue | undefined>(undefined);

export const CookieConsentProvider = ({ children }: PropsWithChildren) => {
    const [decision, setDecision] = useState<ConsentDecision>(() => {
        const existing = readConsentCookie();
        if (!existing) return CONSENT_DECISION.UNDECIDED;
        return existing.analyticsConsentGranted ? CONSENT_DECISION.GRANTED : CONSENT_DECISION.DENIED;
    });

    // Note: the returning-user consent update is emitted earlier and synchronously by
    // CookieConsent's bootstrapCookieConsentMode() call, so it is intentionally NOT
    // repeated here. This provider only handles live accept/deny clicks.
    const persist = useCallback((granted: boolean) => {
        const value = buildConsentValue(granted);
        writeConsentCookie(value);
        pushConsentUpdate(value);
        setDecision(granted ? CONSENT_DECISION.GRANTED : CONSENT_DECISION.DENIED);
    }, []);

    const value = useMemo<IConsentContextValue>(
        () => ({
            decision,
            accept: () => persist(true),
            deny: () => persist(false),
        }),
        [decision, persist],
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};
